import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { toast } from 'react-hot-toast';

interface CreateInvoiceTagsProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  initialTags?: string[];
}

const CreateInvoiceTags = ({ register, errors, watch, setValue, initialTags }: CreateInvoiceTagsProps) => {
  const [tags, setTags] = useState<string[]>(initialTags || []);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync tags with form
  useEffect(() => {
    setValue('tags', tags);
  }, [tags, setValue]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (tags.includes(trimmedTag)) {
      toast.error(`"${trimmedTag}" Tag already exists`);
      return;
    }
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
    }
  };

  const removeTag = (indexToRemove: number) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.includes(',')) {
        // Handle multiple comma-separated tags
        const newTags = inputValue
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag);
        newTags.forEach(addTag);
      } else {
        addTag(inputValue);
      }
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(tags.length - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.includes(',')) {
      // Auto-create tag when comma is typed
      const parts = value.split(',');
      const newTags = parts
        .slice(0, -1)
        .map((tag) => tag.trim())
        .filter((tag) => tag);
      newTags.forEach(addTag);
      setInputValue(parts[parts.length - 1]);
    } else {
      setInputValue(value);
    }
  };

  const handleBlur = () => {
    if (!inputValue.trim()) {
      setInputValue('');
      return;
    }

    if (inputValue.includes(',')) {
      const newTags = inputValue
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag);
      newTags.forEach(addTag);
    } else {
      addTag(inputValue);
    }

    setInputValue('');
  };

  return (
    <div>
      <header className="space-y-3">
        <h2 className="text-xl font-bold leading-[100%]">Tag (Optional)</h2>
        <p className="text-base text-secondary leading-[100%]">The tag is used for invoice search keywords.</p>
      </header>

      <div className="mt-6 space-y-2">
        <label htmlFor="tags" className="text-sm font-medium leading-[100%]">
          Tags
        </label>
        <div
          className="flex flex-wrap items-start gap-2 w-full bg-[#E9ECEF] rounded-[20px] px-4 py-[10px] min-h-[135px] cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          {tags.map((tag, index) => (
            <span key={index} className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 text-sm">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="flex-1 bg-transparent outline-none min-w-[200px]"
            placeholder={tags.length === 0 ? 'Press Enter to add keyword, separate each keyword with comma (,)' : ''}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceTags;
